// My account (21-user-settings.md, "My account"; 17-people.md, "Blocked people"): the profile card in
// the title bar, the picture and display name, and the blocked people. The sections of 27, 24 and
// 23 join there. No chat ID and no contact details.
import { AddPasskeyRow, ManageIdRow } from '../id/ManageId';
import { usePicture } from './picture';
import { UserEvent, type MatrixClient } from 'matrix-js-sdk';
import { useEffect, useState } from 'react';
import { Avatar } from '../blocks/Bits';
import { Button, IconButton } from '../blocks/Button';
import { Dialog, DialogButtons } from '../blocks/Dialog';
import { icons } from '../blocks/icons';
import { useAction } from '../blocks/Wait';
import { mailAccess } from '../core/mail';
import { serverOf } from '../core/session';
import { t } from '../i18n';
import { thumbnail } from '../session/media';
import { useAccountData } from '../session/rooms';
import { chatIcons } from '../chat/icons';
import { Picker } from '../people/Picker';
import { setBlocked } from '../people/PersonCard';
import { Card, Section } from './frame';
import { config, idOnly } from '../core/config';
import { CirclesSection, DeleteIdSection } from '../id/Leave';
import { SignOutEverywhere } from '../id/SignOutEverywhere';
import { readId } from '../id/store';
import { oneForAll } from '../id/onePicture';
import { PasskeyCard } from '../id/Invitations';
import { MailAppsSection, RecoverySection, TwoStepSection } from '../account/sections';
import { personName, personPicture } from '../people/names';

/** The person's own profile changed here: read again (a person in no room hears of it from nowhere else). */
export const meChanged = () => dispatchEvent(new Event('desyr:me'));

/** The person's name and picture, live. */
export function useMe(client: MatrixClient) {
  const me = client.getUserId()!;
  const [, redraw] = useState(0);
  const [profile, setProfile] = useState<{ displayname?: string; avatar_url?: string }>();
  useEffect(() => {
    const user = client.getUser(me);
    const changed = () => redraw((n) => n + 1);
    // The user record names a person by their ID until their profile is known; its picture follows the
    // rooms' membership, which can lag behind a change: the profile, read again on each, says (21, v1.58.49).
    const read = () => void client.getProfileInfo(me).then(setProfile, () => {});
    const pictured = () => (changed(), read());
    read();
    addEventListener('desyr:me', read);
    user?.on(UserEvent.DisplayName, changed);
    user?.on(UserEvent.AvatarUrl, pictured);
    return () => {
      removeEventListener('desyr:me', read);
      user?.off(UserEvent.DisplayName, changed);
      user?.off(UserEvent.AvatarUrl, pictured);
    };
  }, [client, me]);
  const user = client.getUser(me);
  const known = user?.displayName && user.displayName !== me ? user.displayName : profile?.displayname;
  const mxc = profile ? profile.avatar_url : user?.displayName !== me ? user?.avatarUrl : undefined;
  return { id: me, name: known || me.slice(1).split(':')[0], mxc: mxc || undefined };
}

/** The work address, else the chat ID written as an address (never the chat ID itself). */
export const addressOf = (userId: string) => mailAccess(userId)?.email ?? `${userId.slice(1).split(':')[0]}@${serverOf(userId)}`;

export function AccountTitle({ client }: { client: MatrixClient }) {
  const me = useMe(client);
  return (
    <div className="account-title">
      <h2 className="visually-hidden">{t('settings.accountTitle')}</h2>
      <Avatar id={me.id} name={me.name} src={thumbnail(client, me.mxc, 58)} size={58} />
      <div>
        <strong>{me.name}</strong>
        {/* With desyr ID (36): its email, and small under it the open circle's address. */}
        <span>{readId()?.email ?? addressOf(me.id)}</span>
        {readId() && <small>{`${me.id.slice(1).split(':')[0]}@${serverOf(me.id)}`}</small>}
      </div>
      <IconButton label={t('settings.editProfile')} icon={chatIcons.edit} size={20} onClick={() => document.getElementById('settings-profile')?.scrollIntoView({ behavior: 'smooth' })} />
    </div>
  );
}

export function Account({ client }: { client: MatrixClient }) {
  const me = useMe(client);
  const [name, setName] = useState(me.name);
  const [mayName, setMayName] = useState(true);
  const picture = usePicture(client, true);
  useEffect(() => setName(me.name), [me.name]);
  useEffect(() => void client.getCapabilities().then((c) => setMayName(c['m.set_displayname']?.enabled !== false), () => {}), [client]);
  const saveName = useAction(() => client.setDisplayName(name.trim()).then(meChanged), 'save');
  const changed = name.trim() !== me.name && !!name.trim();

  return (
    <>
      {config().desyrId && readId() && <PasskeyCard id={config().desyrId!} />}
      <Section title={t('settings.profile')}>
        <div id="settings-profile" />
        <Card title={t('settings.avatar')} error={picture.error}>
          <div className="settings-picture">
            <Avatar id={me.id} name={me.name} src={thumbnail(client, me.mxc, 58)} size={58} />
            <Button small busy={picture.busy} disabled={!picture.may} onClick={picture.upload}>
              {t('settings.upload')}
            </Button>
            <Button small disabled={!picture.may || picture.busy} onClick={picture.choose}>
              {t('presets.title')}
            </Button>
            {me.mxc && (
              <Button small variant="critical-soft" disabled={!picture.may || picture.busy} onClick={picture.remove}>
                {t('settings.remove')}
              </Button>
            )}
            {picture.parts}
          </div>
          {oneForAll(client) && <p className="settings-quiet-line">{t('id.pictureForAll')}</p>}
        </Card>
        <Card title={t('settings.displayName')} error={saveName.message}>
          <form
            className="field-save"
            onSubmit={(e) => {
              e.preventDefault();
              if (changed && !saveName.busy) void saveName.run();
            }}
          >
            <input aria-label={t('settings.displayName')} value={name} maxLength={256} readOnly={!mayName || saveName.busy} onChange={(e) => setName(e.target.value)} />
            {changed && !saveName.busy && <IconButton label={t('settings.undo')} icon={icons.close} size={18} onClick={() => setName(me.name)} />}
            <Button small type="submit" variant={changed ? 'filled' : 'soft'} className={changed ? 'success' : undefined} busy={saveName.busy} disabled={!changed}>
              {t('box.save')}
            </Button>
          </form>
        </Card>
      </Section>
      {config().desyrId && readId() && <CirclesSection client={client} />}
      {config().desyrId && readId() && (
        <Section title={t('id.passkeys')}>
          <ManageIdRow look="card" />
          <AddPasskeyRow look="card" />
          <SignOutEverywhere />
        </Section>
      )}
      {!idOnly() && <RecoverySection client={client} />}
      {!idOnly() && <TwoStepSection client={client} />}
      <MailAppsSection client={client} />
      <Blocked client={client} />
      {config().desyrId && readId() && <DeleteIdSection client={client} />}
    </>
  );
}

/** Blocked people (17): the list with Unblock, and "Block someone" with the picker. */
function Blocked({ client }: { client: MatrixClient }) {
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState<string>();
  const [error, setError] = useState<string>();
  useAccountData(client, 'm.ignored_user_list');
  const blocked = client.getIgnoredUsers();
  const nameOf = (id: string) => personName(client, id);
  return (
    <Section title={t('people.blockedPeople')}>
      <Card
        icon={chatIcons.block}
        title={t('people.blockedPeople')}
        text={t(blocked.length ? 'people.blockedLine' : 'people.nobodyBlocked')}
        control={<Button small onClick={() => (setError(undefined), setPicking(true))}>{t('people.blockSomeone')}</Button>}
      >
        {blocked.map((id) => (
          <div key={id} className="blocked-row" data-desyr-user={id}>
            <Avatar id={id} name={nameOf(id)} src={thumbnail(client, personPicture(client, id), 32)} size={32} />
            <span>{nameOf(id)}</span>
            <Button
              small
              busy={busy === id}
              disabled={busy === id}
              onClick={() => (setBusy(id), void setBlocked(client, id, false).catch(() => {}).finally(() => setBusy(undefined)))}
            >
              {t('people.unblock')}
            </Button>
          </div>
        ))}
      </Card>
      {picking && (
        <Dialog title={t('people.blockSomeone')} onClose={() => setPicking(false)}>
          <p className="dialog-text">{t('people.blockLine')}</p>
          <Picker
            client={client}
            question={t('people.whoBlock')}
            leaveOut={(id) => blocked.includes(id) || id === client.getUserId()}
            error={error}
            onPick={async (p) => {
              if (!p.userId) return;
              await setBlocked(client, p.userId, true).then(
                () => setPicking(false),
                () => setError(t('people.blockFailed', { name: p.name })),
              );
            }}
          />
          <DialogButtons>
            <Button variant="text" onClick={() => setPicking(false)}>{t('common.cancel')}</Button>
          </DialogButtons>
        </Dialog>
      )}
    </Section>
  );
}
